import * as React from 'react';
import * as Yup from 'yup';
import { Formik, FastField } from 'formik';
import { FormikTextField } from './FormikTextField';
import { Box, Button, Dialog, DialogTitle, InputAdornment, DialogContent, IconButton, Paper, Tooltip, Typography, DialogActions, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { serviceLocator } from '../services';
import { DebouncedTextField } from './DebouncedTextField';
import { Remove, ExpandMore, ContentCopyOutlined, AddOutlined, DataObjectOutlined } from '@mui/icons-material';
import { secondaryColor } from '../themes';

const websiteService = serviceLocator.getWebsiteService();

interface Props {
    websiteKey: string;
    path?: string;
}

type VariablesArrayItem = {key: string, value: string, state:"used"|"unused"|"new"};
type VariablesArray = Array<VariablesArrayItem>;

interface State {
    variables: VariablesArray;
    versionId: string;
    showEntryModal: boolean;
}

export class WebsiteVariables extends React.Component<Props,State> {

    _hasChanges: boolean = false;

    state: State = {
        variables: [],
        versionId: 'NOT_LOADED',
        showEntryModal: false,
    }

    groupBy = function<T>(xs: T[], keyExtractor: (item: T)=>any) {
        return xs.reduce(function(rv: any, x: any) {
            (rv[keyExtractor(x)] = rv[keyExtractor(x)] || []).push(x);
            return rv;
        }, {});
    };

    setVariablesState = (variablesIn: {variables: { used: any, unused: any }, versionId: string }|null) => {
        const { used, unused } = { used:{}, unused:{}, ...(variablesIn||{variables: null}).variables };
        const versionId: string = variablesIn!=null ? variablesIn.versionId : '';
        const variables: VariablesArray =[
            ...Object.keys(used).map(key => ({ key, value: used[key], state:"used" } as VariablesArrayItem)),
            ...Object.keys(unused).map(key => ({  key, value: unused[key], state:"unused" } as VariablesArrayItem))
        ];
        variables.sort((a,b)=>{ return a.key.toLowerCase() > b.key.toLowerCase() ? 1 : -1; });
        this.setState({
            variables,
            versionId
        });
    }

    componentDidMount = async () => {
        const variables = (await websiteService.getVariables(this.props.websiteKey, this.props.path));
        this.setVariablesState(variables);
    }

    componentWillUnmount = () =>{
        if(this._hasChanges){
            if(window.confirm(`Do you want to save the website variables changes?\nVariables: (${this.props.path||"global"})` )){
                this.saveAndUpdate(false);
            }
        }
    }

    public saveAndUpdate = async (refresh: boolean = true) => {
        var dict: {[key:string]: string} = {};
        this.state.variables.forEach(item =>{ dict[item.key] = item.value });
        const {versionId} = this.state;
        const variables = (await websiteService.putVariables(this.props.websiteKey, this.props.path||null, { variables: dict, versionId }));
        if(refresh) this.setVariablesState(variables);
        this._hasChanges = false;
    }

    handleAddClick = async (e: any) => {
        this.setState({showEntryModal: true});
    }
 
    handleNewItemSubmit = async (values: any, { setSubmitting, resetForm }: any) =>{
        const key = values.key.trim();
        if(this.state.variables.findIndex(x => x.key===key)===-1){
            this.setState({
                variables: [...this.state.variables, { key, state:'new', value:'' }],
                showEntryModal: false
            });
            resetForm();
        }
        setSubmitting(false);
    }

    handleTextFieldChange = (e: any) => {
        this._hasChanges = true;
        const key = e.target.dataset.key;
        const index = this.state.variables.findIndex(x => x.key === key);
        const updateItem = this.state.variables[index];
        if(updateItem!=null){
            const { state } = updateItem;
            const variables = [ ...this.state.variables ];
            variables.splice(index, 1, { key, state, value: e.target.value });
            this.setState({ variables })
        }
    }

    handleDeleteEntryClick = (e: any) => {
        const key = e.currentTarget.dataset.key;
        const index = this.state.variables.findIndex(x => x.key===key);
        const variables = [ ...this.state.variables ];
        variables.splice(index, 1);
        this.setState({ variables })
    }

    handleCopyEntryClick = (e: any) => {
        const key = `{{ ${e.currentTarget.dataset.key} }}`;
        console.log('copy', key)
        navigator.clipboard.writeText(key)
    }

    handleDialogClose = (e: any) => {
        this.setState({showEntryModal: false});
    }

    renderDialog(){
        return (
            <Formik
                initialValues={{ key: '' }}
                onSubmit={this.handleNewItemSubmit} validationSchema={
                    Yup.object().shape({ key: Yup.string().required('Required.') }).required()
                }>
                { props => {
                    const { handleSubmit, isSubmitting, submitForm } = props;
    
                    return (
                        <Dialog
                            onClose={this.handleDialogClose}
                            open={this.state.showEntryModal}>
                            <DialogTitle>Create New Page</DialogTitle>
                            <DialogContent>
                                <form onSubmit={handleSubmit}>
                                    <FastField autoFocus={true} margin="normal" name="key" label="Entry Key" helperText='To group an entry, add a prefix followeb by ":".' component={FormikTextField} />
                                    <button disabled={isSubmitting} type="submit" onClick={submitForm} style={{width:1, height:1, visibility:'hidden'}}>Create</button>
                                </form>
                            </DialogContent>
                            <DialogActions>
                                <Button disabled={isSubmitting} color="primary" type="submit" value="create" onClick={submitForm}>Create</Button>
                            </DialogActions>
                        </Dialog>
                    )
                }}
            </Formik>
        );
    }

    extractPrefix = (item: VariablesArrayItem)=>{
        const parts = item.key.split(':');
        if(parts.length===1) return "Default";
        return parts[0].trim();
    }

    render(){

        var group = this.groupBy(this.state.variables, this.extractPrefix);
        var _group: Array<{ prefix: string, items: VariablesArrayItem[] }> = [];
        for(var key in group){
            _group.push({prefix: key, items: group[key]});
        }

        const hasNoVariables = _group.length === 0;

        return (
            <React.Fragment>
                {hasNoVariables ? (
                    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed', mb: 2 }}>
                        <Box sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            backgroundColor: 'action.hover',
                            color: 'text.secondary',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1.5,
                        }}>
                            <DataObjectOutlined fontSize="small" />
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            No variables yet
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            Add a key to use as <code>{'{{ key }}'}</code> across this {this.props.path ? 'page' : 'site'}.
                        </Typography>
                    </Paper>
                ) : _group.map(x => (
                    <Accordion
                        key={x.prefix}
                        disableGutters
                        elevation={0}
                        sx={{
                            border: 'solid 1px',
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                            '&:before': { display: 'none' },
                            '&.Mui-expanded': { mb: 1 },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{
                                minHeight: 44,
                                '&.Mui-expanded': { minHeight: 44 },
                                '& .MuiAccordionSummary-content': { my: 1 },
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{x.prefix}</Typography>
                            <Typography variant="caption" sx={{ ml: 1, color: 'text.disabled', alignSelf: 'center' }}>
                                {x.items.length}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <Box sx={{ width: '100%' }}>
                                {x.items.map((x, i) => (
                                    <DebouncedTextField
                                        style={x.state === 'unused' ? { opacity: .55 } : null}
                                        key={x.key} name={`variable:${x.key}`} label={x.key} value={x.value || ''}
                                        margin="dense"
                                        fullWidth={true}
                                        slotProps={{
                                            htmlInput: { "data-key": x.key },
                                            inputLabel: {
                                                shrink: true,
                                                style: x.state === 'new' ? { color: secondaryColor[500] } : null
                                            },
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end" sx={{ gap: 0 }}>
                                                        <Tooltip title="Copy {{ key }}">
                                                            <IconButton
                                                                size="small"
                                                                data-key={x.key}
                                                                onClick={this.handleCopyEntryClick}
                                                            >
                                                                <ContentCopyOutlined fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {x.state !== "used" && (
                                                            <Tooltip title="Remove">
                                                                <IconButton
                                                                    size="small"
                                                                    data-key={x.key}
                                                                    onClick={this.handleDeleteEntryClick}
                                                                    color="error"
                                                                >
                                                                    <Remove fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </InputAdornment>
                                                )
                                            }
                                        }}
                                        onChange={this.handleTextFieldChange} />
                                ))}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))}
                <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={<AddOutlined />}
                    onClick={this.handleAddClick}
                    sx={{ mt: 1 }}
                >
                    Add Entry
                </Button>
                {this.renderDialog()}
            </React.Fragment>
        );
    }

}